import { Mesh, PlaneGeometry, TorusGeometry, Vector3 } from 'three';
import { Ability, AbilityPhase } from './Ability.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { DecalType } from '../effects/GroundDecals.js';
import { LAYER } from '../core/Layers.js';
import { frame } from '../core/FrameUniforms.js';
import { settings } from '../config/settings.js';
import { getColor } from '../utils/color.js';
import { Easing, randRange, saturate } from '../utils/math.js';
import {
  createCamouflageGroundMaterial,
  createCamouflageRingMaterial,
  syncCamouflageMaterial
} from '../materials/CamouflageMaterial.js';

const TAU = Math.PI * 2;
const _emit = {};
const _pos = new Vector3();
const _dir = new Vector3();

/**
 * Véu Predatório is a self-bound stealth cast: the assassin dissolves at the
 * end of a short preparation, stays visually absent inside a moving smoke and
 * shadow field, then returns through a narrow scan-line reveal. The sandbox has no
 * combat-status host, so the character opacity is the visual contract here.
 */
export class CamouflageAbility extends Ability {
  constructor(context) {
    super('camouflage', context);
  }

  createShaders() {
    this.ringGeometry = new TorusGeometry(1, 0.025, 8, 64);
    this.groundGeometry = new PlaneGeometry(1, 1, 1, 1).rotateX(-Math.PI / 2);

    this.ringMaterial = createCamouflageRingMaterial();
    this.groundMaterial = createCamouflageGroundMaterial();

    this.ring = new Mesh(this.ringGeometry, this.ringMaterial);
    this.ring.layers.set(LAYER.VFX);
    this.ring.renderOrder = 12;
    this.ring.frustumCulled = false;
    this.group.add(this.ring);

    this.echoRing = new Mesh(this.ringGeometry, this.ringMaterial);
    this.echoRing.layers.set(LAYER.VFX);
    this.echoRing.renderOrder = 12;
    this.echoRing.frustumCulled = false;
    this.group.add(this.echoRing);

    this.ground = new Mesh(this.groundGeometry, this.groundMaterial);
    this.ground.layers.set(LAYER.VFX);
    this.ground.renderOrder = 6;
    this.ground.frustumCulled = false;
    this.group.add(this.ground);

    this._anchor = new Vector3();
    this._seed = 0;
    this._entered = false;
    this._pulseTime = 0;
    this._state = { fade: 1, reveal: 0, pulse: 0, opacity: 1, seed: 0 };
    this._ringState = { fade: 1, reveal: 0, pulse: 0, opacity: 1, seed: 0 };
    this._groundState = { fade: 1, reveal: 0, pulse: 0, opacity: 1, seed: 0 };
  }

  createParticles() {
    const particles = this.ctx.particles;

    this.motes = particles.get('camouflage.motes', {
      capacity: 2800,
      shape: ParticleShape.SOFT,
      additive: true,
      curl: true,
      swirl: true,
      softFade: 0.4
    });
    this.motes.uniforms.uDrag.value = 1.35;
    this.motes.uniforms.uEndSize.value = 0.16;
    this.motes.uniforms.uSizeIn.value = 0.035;
    this.motes.uniforms.uFadeIn.value = 0.05;
    this.motes.uniforms.uFadeOut.value = 0.44;

    this.shimmers = particles.get('camouflage.shimmers', {
      capacity: 2200,
      shape: ParticleShape.STREAK,
      additive: true,
      curl: true,
      stretch: true,
      softFade: 0.28
    });
    this.shimmers.uniforms.uDrag.value = 0.8;
    this.shimmers.uniforms.uEndSize.value = 0.18;
    this.shimmers.uniforms.uSizeIn.value = 0.02;
    this.shimmers.uniforms.uFadeIn.value = 0.025;
    this.shimmers.uniforms.uFadeOut.value = 0.52;

    this.haze = particles.get('camouflage.haze', {
      capacity: 1400,
      shape: ParticleShape.SMOKE,
      additive: false,
      curl: true,
      softFade: 1.1
    });
    this.haze.uniforms.uDrag.value = 1.8;
    this.haze.uniforms.uEndSize.value = 2.6;
    this.haze.uniforms.uSizeIn.value = 0.1;
    this.haze.uniforms.uFadeIn.value = 0.12;
    this.haze.uniforms.uFadeOut.value = 0.38;

    this.mist = particles.get('camouflage.mist', {
      capacity: 1800,
      shape: ParticleShape.SMOKE,
      additive: false,
      curl: true,
      softFade: 1.4
    });
    this.mist.uniforms.uDrag.value = 1.45;
    this.mist.uniforms.uEndSize.value = 3.4;
    this.mist.uniforms.uSizeIn.value = 0.06;
    this.mist.uniforms.uFadeIn.value = 0.16;
    this.mist.uniforms.uFadeOut.value = 0.48;

    this.intakeEmitter = new RateEmitter();
    this.moteEmitter = new RateEmitter();
    this.shimmerEmitter = new RateEmitter();
    this.hazeEmitter = new RateEmitter();
    this.mistEmitter = new RateEmitter();
  }

  get instanceCount() {
    return 3;
  }

  get impactDuration() {
    return Math.max(0.05, settings.camouflage.cloakDuration * settings.global.lifetime);
  }

  get fadeDuration() {
    return Math.max(0.05, settings.camouflage.revealDuration);
  }

  lightShimmer() {
    const c = settings.camouflage;
    return 0.74 + 0.26 * (0.5 + 0.5 * Math.sin(this.age * c.pulseSpeed * TAU));
  }

  _casterPoint(out) {
    return out.copy(this.ctx.character?.position ?? this.origin).setY(0);
  }

  _syncParticleUniforms() {
    const c = settings.camouflage;
    const g = settings.global;
    this.motes.setGradient(getColor(c.colorCore), getColor(c.colorEdge), getColor(c.colorShadow), getColor(c.colorDeep));
    this.motes.uniforms.uGravity.value.set(0, c.moteRise, 0);
    this.motes.uniforms.uSizeScale.value = c.moteSize * g.particleSize * 7;
    this.motes.uniforms.uLifeScale.value = c.moteLifetime * 0.5 * g.particleLifetime;
    this.motes.uniforms.uSpeedScale.value = g.particleSpeed;
    this.motes.uniforms.uOpacity.value = c.moteOpacity * g.opacity;
    this.motes.uniforms.uGlow.value = c.moteGlow * g.glow;
    this.motes.uniforms.uTurbulence.value = c.particleTurbulence * g.turbulence;
    this.motes.uniforms.uSwirl.value = c.particleSwirl;
    this.motes.uniforms.uSwirlExpand.value = c.particleSwirlExpand;

    this.shimmers.setGradient(getColor(c.colorCore), getColor(c.colorEdge), getColor(c.colorShadow), getColor(c.colorDeep));
    this.shimmers.uniforms.uGravity.value.set(0, c.shimmerGravity, 0);
    this.shimmers.uniforms.uSizeScale.value = c.shimmerSize * g.particleSize * 7;
    this.shimmers.uniforms.uLifeScale.value = c.shimmerLifetime * 0.5 * g.particleLifetime;
    this.shimmers.uniforms.uSpeedScale.value = g.particleSpeed;
    this.shimmers.uniforms.uOpacity.value = c.shimmerOpacity * g.opacity;
    this.shimmers.uniforms.uGlow.value = c.shimmerGlow * g.glow;
    this.shimmers.uniforms.uStretch.value = c.shimmerStretch;
    this.shimmers.uniforms.uTurbulence.value = c.particleTurbulence * g.turbulence;

    this.haze.setGradient(getColor(c.colorHazeA), getColor(c.colorHazeB), getColor(c.colorHazeC), getColor(c.colorDeep));
    this.haze.uniforms.uGravity.value.set(0, c.hazeRise, 0);
    this.haze.uniforms.uSizeScale.value = c.hazeSize * g.particleSize;
    this.haze.uniforms.uLifeScale.value = c.hazeLifetime * 0.5 * g.particleLifetime;
    this.haze.uniforms.uSpeedScale.value = c.hazeSpeed * g.particleSpeed;
    this.haze.uniforms.uOpacity.value = c.hazeOpacity * g.opacity;
    this.haze.uniforms.uTurbulence.value = c.hazeTurbulence * g.turbulence;

    this.mist.setGradient(getColor(c.colorMistA), getColor(c.colorMistB), getColor(c.colorMistC), getColor(c.colorDeep));
    this.mist.uniforms.uGravity.value.set(0, c.mistRise, 0);
    this.mist.uniforms.uSizeScale.value = c.mistSize * g.particleSize;
    this.mist.uniforms.uLifeScale.value = c.mistLifetime * 0.5 * g.particleLifetime;
    this.mist.uniforms.uSpeedScale.value = c.mistSpeed * g.particleSpeed;
    this.mist.uniforms.uOpacity.value = c.mistOpacity * g.opacity;
    this.mist.uniforms.uTurbulence.value = c.mistTurbulence * g.turbulence;
  }

  onSpawn() {
    const c = settings.camouflage;
    this._casterPoint(this._anchor);
    this._seed = Math.random() * 100;
    this._entered = false;
    this._pulseTime = 0;
    for (const emitter of [this.intakeEmitter, this.moteEmitter, this.shimmerEmitter, this.hazeEmitter, this.mistEmitter]) {
      emitter.reset();
    }
    this._setCharacterVisual(1, 0);
    this._syncParticleUniforms();
    this._syncVisual(0.05, 0.28, 0.2);
    this._prepFx(0);
    this.position.copy(this._anchor).setY(c.cloakHeight * 0.7);
  }

  onTravel(dt) {
    const c = settings.camouflage;
    const preparation = saturate(this.u);
    this._casterPoint(this._anchor);
    this._syncParticleUniforms();
    this._syncVisual(preparation, 0.32 + preparation * 0.68, 0.2 + preparation * 0.8);
    this._prepFx(dt);
    this.position.copy(this._anchor).setY(c.cloakHeight * 0.7);
    this.ctx.shake.rumble(c.prepRumble * settings.global.cameraShake, dt);
  }

  onImpact() {
    const c = settings.camouflage;
    const g = settings.global;
    this._entered = true;
    this._pulseTime = 0;
    this._casterPoint(this._anchor);
    this._setCharacterVisual(c.hiddenOpacity, c.cloakCompression);
    this._syncVisual(1, 1, 1);

    _pos.copy(this._anchor).setY(c.burstHeight);
    this.ctx.bursts.spawn(BurstMode.AIR, _pos, {
      radius: c.burstRadius * 0.18,
      endRadius: c.burstRadius * g.explosionIntensity,
      life: c.burstLife,
      intensity: c.burstIntensity,
      opacity: c.burstOpacity,
      fresnel: 2.1,
      displace: c.burstDisplace,
      colorA: getColor(c.colorDeep),
      colorB: getColor(c.colorEdge),
      colorC: getColor(c.colorCore)
    });
    this.ctx.decals.spawn(DecalType.SHOCKWAVE, this._anchor, {
      radius: c.impactRingRadius,
      life: c.impactRingLife,
      width: c.impactRingWidth,
      intensity: c.impactRingIntensity,
      colorA: getColor(c.colorShadow),
      colorB: getColor(c.colorEdge)
    });
    this._emitBurst(c.vanishMotes, this.motes, c.moteSize * 0.9, c.vanishSpeed, c.moteLifetime);
    this._emitBurst(c.vanishShimmers, this.shimmers, c.shimmerSize * 1.2, c.shimmerSpeed, c.shimmerLifetime);
    this.ctx.flash.trigger(getColor(c.colorFlash), c.impactFlash * g.explosionIntensity);
    this.ctx.shake.add(c.impactShake * g.explosionIntensity * g.cameraShake, 1 / Math.max(0.1, c.shakeDuration), 22);
    this.lightBoost = c.lightIntensity * c.impactLightBoost * g.explosionIntensity;
  }

  onFade(dt, t) {
    const c = settings.camouflage;
    const g = settings.global;
    this._casterPoint(this._anchor);
    this._pulseTime += dt;
    const reveal = t <= 1 ? 0 : Easing.outCubic(saturate(t - 1));
    const fade = t <= 1 ? 1 : 1 - reveal;
    const pulse = 0.34 + Math.sin(this.age * c.pulseSpeed * TAU) * c.pulseAmount * fade;
    const opacity = t <= 1 ? c.hiddenOpacity : c.hiddenOpacity + (1 - c.hiddenOpacity) * reveal;
    const compression = c.cloakCompression * (1 - reveal);

    this._setCharacterVisual(opacity, compression);
    this._syncParticleUniforms();
    this._syncVisual(1, fade, 1);
    this._cloakFx(dt, fade, pulse);
    this.position.copy(this._anchor).setY(c.cloakHeight * (0.62 + pulse * 0.12));
    if (t <= 1) this.ctx.shake.rumble(c.cloakRumble * g.cameraShake, dt);
  }

  _syncVisual(reveal, fade, intensity) {
    const c = settings.camouflage;
    const state = this._state;
    const pulse = 0.35 + Math.sin(this.age * c.pulseSpeed * TAU) * c.pulseAmount;
    state.fade = Math.max(0, fade);
    state.reveal = saturate(reveal);
    state.pulse = Math.max(0, pulse * intensity);
    state.opacity = intensity;
    state.seed = this._seed;

    const visible = state.fade > 0.002;

    this.ring.position.copy(this._anchor);
    this.ring.position.y += c.ringHeight + Math.sin(this.age * c.ringBobSpeed) * c.ringBobAmount;
    this.ring.rotation.set(Math.PI * 0.5, this.age * c.ringSpin, 0);
    this.ring.scale.setScalar(c.ringRadius * (1 + state.pulse * 0.08));
    this.ring.visible = visible;
    const ringState = this._ringState;
    ringState.fade = state.fade;
    ringState.reveal = state.reveal;
    ringState.pulse = state.pulse;
    ringState.opacity = c.ringOpacity * intensity;
    ringState.seed = state.seed;
    syncCamouflageMaterial(this.ringMaterial, c, ringState);

    this.echoRing.position.copy(this._anchor);
    this.echoRing.position.y += c.echoRingHeight - Math.sin(this.age * c.ringBobSpeed * 0.83) * c.ringBobAmount * 0.7;
    this.echoRing.rotation.set(Math.PI * 0.5, -this.age * c.echoRingSpin, 0);
    this.echoRing.scale.setScalar(c.echoRingRadius * (1 + state.pulse * 0.05));
    this.echoRing.visible = visible;

    this.ground.position.copy(this._anchor);
    this.ground.position.y = c.groundHeight;
    this.ground.scale.setScalar(c.groundRadius * 2);
    this.ground.visible = visible;
    const groundState = this._groundState;
    groundState.fade = state.fade;
    groundState.reveal = state.reveal;
    groundState.pulse = state.pulse;
    groundState.opacity = c.groundOpacity * intensity;
    groundState.seed = state.seed;
    syncCamouflageMaterial(this.groundMaterial, c, groundState);
  }

  _prepFx(dt) {
    const c = settings.camouflage;
    const count = Math.round(this.intakeEmitter.tick(dt, c.intakeRate * (0.28 + this.u * 0.72)));
    if (count <= 0) return;
    const per = Math.max(1, Math.ceil(count / 5));
    let left = count;
    while (left > 0) {
      const angle = Math.random() * TAU;
      const radius = c.intakeRadius * randRange(0.72, 1.15);
      _pos.set(
        this._anchor.x + Math.cos(angle) * radius,
        randRange(0.12, c.cloakHeight * 1.35),
        this._anchor.z + Math.sin(angle) * radius
      );
      _dir.copy(this._anchor).setY(randRange(0.65, 1.25)).sub(_pos).normalize();
      this._emit(this.motes, Math.min(per, left), _pos, _dir, radius * 0.14, c.intakeSpeed,
        c.moteSize, c.intakeLifetime, 0.18, this._anchor);
      left -= per;
    }
  }

  _cloakFx(dt, fade, pulse) {
    const c = settings.camouflage;
    const moteCount = Math.round(this.moteEmitter.tick(dt, c.moteRate * fade * (0.72 + pulse)));
    if (moteCount > 0) {
      _pos.copy(this._anchor).setY(randRange(0.16, c.cloakHeight * 1.62));
      _dir.set(randRange(-0.3, 0.3), randRange(0.4, 1.1), randRange(-0.3, 0.3)).normalize();
      this._emit(this.motes, moteCount, _pos, _dir, c.cloakRadius * 0.8, c.moteSpeed * (0.8 + pulse),
        c.moteSize, c.moteLifetime, 0.8, this._anchor, c.moteSpin);
    }

    const shimmerCount = Math.round(this.shimmerEmitter.tick(dt, c.shimmerRate * fade * (0.6 + pulse)));
    if (shimmerCount > 0) {
      const angle = Math.random() * TAU;
      _pos.set(
        this._anchor.x + Math.cos(angle) * c.ringRadius,
        c.ringHeight + Math.sin(this.age * c.ringBobSpeed) * c.ringBobAmount,
        this._anchor.z + Math.sin(angle) * c.ringRadius
      );
      _dir.set(-Math.sin(angle), randRange(-0.18, 0.3), Math.cos(angle)).normalize();
      this._emit(this.shimmers, shimmerCount, _pos, _dir, 0.18, c.shimmerSpeed,
        c.shimmerSize, c.shimmerLifetime, 0.28, this._anchor);
    }

    const hazeCount = Math.round(this.hazeEmitter.tick(dt, c.hazeRate * fade * (0.72 + pulse * 0.18)));
    if (hazeCount > 0) {
      _pos.copy(this._anchor).setY(0.08);
      _dir.set(0, 1, 0);
      this._emit(this.haze, hazeCount, _pos, _dir, c.groundRadius * 0.52, c.hazeSpeed,
        c.hazeSize, c.hazeLifetime, 0.85, null, c.hazeSpin);
    }

    const mistCount = Math.round(this.mistEmitter.tick(dt, c.mistRate * fade * (0.82 + pulse * 0.24)));
    if (mistCount > 0) {
      _pos.copy(this._anchor).setY(randRange(0.18, c.cloakHeight * 1.18));
      _dir.set(randRange(-0.32, 0.32), randRange(0.05, 0.42), randRange(-0.32, 0.32)).normalize();
      this._emit(this.mist, mistCount, _pos, _dir, c.cloakRadius * 0.62, c.mistSpeed,
        c.mistSize, c.mistLifetime, c.mistSpread, null, c.mistSpin);
    }
  }

  _emitBurst(count, system, size, speed, life) {
    if (count <= 0) return;
    _pos.copy(this._anchor).setY(settings.camouflage.burstHeight);
    _dir.set(0, 1, 0);
    this._emit(system, count, _pos, _dir, settings.camouflage.cloakRadius * 0.5, speed,
      size, life, 1);
  }

  _emit(system, count, position, direction, radius, speed, size, life, spread, anchor = null, spin = 0) {
    const c = settings.camouflage;
    _emit.position = position;
    _emit.radius = radius;
    _emit.direction = direction;
    _emit.speed = speed;
    _emit.speedVariance = 0.55;
    _emit.spread = spread;
    _emit.inherit = null;
    _emit.anchor = anchor;
    _emit.size = size;
    _emit.sizeVariance = 0.58;
    _emit.life = life;
    _emit.lifeVariance = 0.42;
    _emit.spin = spin;
    _emit.tint = null;
    _emit.time = frame.uTime.value;
    system.emit(Math.round(count * settings.global.particleCount), _emit);
  }

  _setCharacterVisual(opacity, compression) {
    this.ctx.character?.setShadowTransition?.(opacity, compression);
  }

  onDestroy() {
    this._entered = false;
    this._setCharacterVisual(1, 0);
    this.ring.visible = false;
    this.echoRing.visible = false;
    this.ground.visible = false;
    this.ringMaterial.uniforms.uFade.value = 0;
    this.groundMaterial.uniforms.uFade.value = 0;
  }

  dispose() {
    this.ringGeometry.dispose();
    this.groundGeometry.dispose();
    this.ringMaterial.dispose();
    this.groundMaterial.dispose();
    super.dispose();
  }
}
